import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SUBJECTS } from '../../config/subjects';
import { useAuth } from '../Auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NBDHE_SUBJECTS = SUBJECTS.filter(s => s.exam === 'nbdhe');
const LA_BOARD_SUBJECTS = SUBJECTS.filter(s => s.exam === 'la-board');

function AcademyLanding() {
  const [studyGuideCounts, setStudyGuideCounts] = useState({});
  const [, setTotalStudyGuides] = useState(0);
  const { totalQuizzesTaken, lastQuizScore } = useAuth();

  useEffect(() => {
    const fetchStudyGuideCounts = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        const response = await fetch(`${API_BASE_URL}/api/study-guides`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const guides = data.guides || [];

          const counts = {};
          guides.forEach(guide => {
            const subjectId = guide.subjectId;
            counts[subjectId] = (counts[subjectId] || 0) + 1;
          });

          setStudyGuideCounts(counts);
          setTotalStudyGuides(guides.length);
        }
      } catch (error) {
        console.error('Error fetching study guide counts:', error);
      }
    };

    fetchStudyGuideCounts();
  }, []);

  return (
    <div className="min-h-screen bg-white font-quicksand">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-16 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-4">
          StudyPass Essentials
        </h1>
        <p className="text-xl text-study-black max-w-2xl mx-auto mb-2">
          Master content with comprehensive study materials organized by subject
        </p>
        <p className="text-study-black text-lg">
          NBDHE & LA Board Exam Prep<br />
          3,000+ Practice Questions<br />
          2,000+ Flashcards<br />
          100+ Study Guides<br />
          150+ Video Lessons
        </p>
      </div>

      {/* Your Progress Stats Bar */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-gradient-to-r from-study-yellow to-study-pink rounded-2xl p-6 shadow-md">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <span className="text-3xl">📊</span>
              <h3 className="text-xl font-bold text-study-black">Your Progress</h3>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              <div className="text-center">
                <p className="text-3xl font-bold text-study-black">{totalQuizzesTaken}</p>
                <p className="text-sm text-study-black">Quizzes Taken</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-study-black">
                  {lastQuizScore !== null ? `${lastQuizScore}%` : '—'}
                </p>
                <p className="text-sm text-study-black">Last Score</p>
              </div>
              <Link
                to="/progress"
                className="bg-study-teal text-study-black font-bold px-5 py-2 rounded-lg hover:bg-white transition-all"
              >
                See More →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* NBDHE Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-study-black">NBDHE Exam Prep</h2>
          <p className="text-gray-600 mt-1">National Board Dental Hygiene Examination — 12 subjects</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {NBDHE_SUBJECTS.map((subject) => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              studyGuideCount={studyGuideCounts[subject.wixId] || 0}
            />
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 py-2">
        <hr className="border-gray-200" />
      </div>

      {/* LA Board Exam Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-study-black">LA Board Exam Prep</h2>
          <p className="text-gray-600 mt-1">Written Local Anesthesia Board Examination</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {LA_BOARD_SUBJECTS.map((subject) => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              studyGuideCount={studyGuideCounts[subject.wixId] || 0}
            />
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="bg-study-yellow py-12 px-6 text-center">
        <h2 className="text-3xl font-bold text-study-black mb-4">
          Ready to ace your exams?
        </h2>
        <p className="text-study-black text-lg mb-6">
          Get unlimited access to all study materials with StudyPass Premium
        </p>
        <Link
          to="/pricing"
          className="inline-block bg-study-teal text-study-black font-bold text-lg py-4 px-8 rounded-full hover:bg-study-pink transition-all transform hover:scale-105 shadow-lg"
        >
          Upgrade to Premium
        </Link>
      </div>
    </div>
  );
}

function SubjectCard({ subject, studyGuideCount }) {
  const availableResources = [
    subject.hasStudyGuides && 'Study Guides',
    subject.hasFlashcards && 'Flashcards',
    subject.hasVideos && 'Videos'
  ].filter(Boolean);

  return (
    <Link
      to={`/academy/${subject.id}`}
      className="block bg-white rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:scale-105 overflow-hidden"
    >
      {/* Card Header with Gradient */}
      <div className={`bg-gradient-to-br ${subject.color} p-6 text-center`}>
        <div className="text-6xl mb-3">{subject.icon}</div>
        <h3 className="text-2xl font-bold text-white">{subject.name}</h3>
      </div>

      {/* Card Body */}
      <div className="p-6">
        <p className="text-study-black text-sm mb-4 line-clamp-2">
          {subject.description}
        </p>

        {/* Resource Counts */}
        <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
          <div className="flex items-center">
            <span className="font-semibold text-study-black">{subject.questionCount}</span>
            <span className="ml-1">Questions</span>
          </div>
          <div className="flex items-center">
            <span className="font-semibold text-study-black">{studyGuideCount}</span>
            <span className="ml-1">Study Guides</span>
          </div>
        </div>

        {/* Available Resources Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {availableResources.map((resource) => (
            <span
              key={resource}
              className="inline-block bg-study-teal text-study-black text-xs px-3 py-1 rounded-full font-semibold"
            >
              {resource}
            </span>
          ))}
          {availableResources.length === 0 && (
            <span className="inline-block bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
              Coming Soon
            </span>
          )}
        </div>

        {/* Explore Button */}
        <button className="w-full bg-study-pink text-study-black font-bold py-3 rounded-lg hover:bg-study-yellow transition-all">
          Explore {subject.name}
        </button>
      </div>
    </Link>
  );
}

export default AcademyLanding;
